/** @format */
import { Inject, Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import { CourseUnitRealisation, Enrolment, EnrolmentRight, OtmId } from 'common-typescript/types';
import { Observable, of, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppErrorHandler, EnrolmentRightEntityService, isCourseUnitEnrolmentRight } from 'sis-components';
import { COURSE_UNIT_INFO_MODAL } from '../../ajs-upgraded-modules';
import { OpenUniversityOfferingPreselection } from '../../open-university/study-offering/open-university-offering/open-university-offering.component';

@Injectable({ providedIn: 'root' })
export class StudentCourseUnitInfoModalService {
    constructor(
        private readonly _enrolmentRightEntityService: EnrolmentRightEntityService,
        @Inject(COURSE_UNIT_INFO_MODAL) private readonly _courseUnitInfoModal: any,
        private readonly _appErrorHandler: AppErrorHandler,
    ) {}

    /**
     * Open the course unit info modal on the basic info tab.
     */
    openCourseUnit(courseUnitId: OtmId): void {
        this._courseUnitInfoModal.showCourseUnit(courseUnitId);
    }

    /**
     * Open the course unit info modal in the completion methods tab. If a `ValidatablePlan` is provided,
     * the modal shows additional information related to the course unit in the context of that plan
     * (e.g. completion method selection and attainment status).
     */
    openCourseUnitCompletionMethods(courseUnitId: OtmId, validatablePlan?: ValidatablePlan): void {
        this._courseUnitInfoModal.showCompletionMethodsForCourseUnit(courseUnitId, validatablePlan);
    }

    /**
     * If the enrolment is an open university enrolment,
     * open the course unit info modal on the open university offering tab with the purchased product pre-selected
     * and the enrolled assessment item and course unit realisation focused.
     * Otherwise, opens the completion methods tab.
     */
    openCourseUnitModalForEnrolment(courseUnitId: OtmId, courseUnitRealisation: CourseUnitRealisation, enrolment: Enrolment): void {
        this.getOpenUniversityProductId(enrolment)
            .pipe(
                take(1),
                map((openUniversityProductId: OtmId | null) => {
                    if (openUniversityProductId) {
                        this._courseUnitInfoModal.showOpenUniversityPurchase(courseUnitId, courseUnitRealisation, <
                            OpenUniversityOfferingPreselection
                        >{
                            selectedOpenUniversityProductId: openUniversityProductId,
                            // Enrolment.assessmentItemId can be null, but it's OK
                            focusedAssessmentItemId: enrolment.assessmentItemId,
                            focusedCourseUnitRealisationId: courseUnitRealisation.id,
                        });
                    } else {
                        this._courseUnitInfoModal.showCourseUnitRealisationForCourseUnit(courseUnitId, courseUnitRealisation);
                    }
                }),
                this._appErrorHandler.defaultErrorHandler(),
            )
            .subscribe();
    }

    private getOpenUniversityProductId(enrolment: Enrolment): Observable<OtmId | null> {
        if (!enrolment.enrolmentRightId) {
            return of(null);
        }

        return this._enrolmentRightEntityService
            .getById(enrolment.enrolmentRightId)
            .pipe(
                map(
                    (enrolmentRight: EnrolmentRight) =>
                        (isCourseUnitEnrolmentRight(enrolmentRight) && enrolmentRight.openUniversityProductId) ?? null,
                ),
            );
    }
}
